import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './useAuth';

/**
 * ADR-018 — lista de launch_deals.
 *
 * Ajuste 1 Gemini (lição SET 2A): aguardar auth carregar antes do query.
 * Ajuste 2 Gemini: filtro explícito por business_unit_id (cinto-suspensório-elástico
 *   além do RLS + chk_launch_deals_bu).
 *
 * Filtros opcionais via params: { stage, status, empresa_id, owner }
 */
export function useLaunchDeals(filters = {}) {
  const { user, loading: authLoading } = useAuth();
  const [deals, setDeals] = useState([]);
  const [launchesUnitId, setLaunchesUnitId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Resolve UUID de business_unit launches uma vez
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('business_units')
      .select('id')
      .eq('code', 'launches')
      .single()
      .then(({ data }) => {
        if (!cancelled && data?.id) setLaunchesUnitId(data.id);
      });
    return () => { cancelled = true; };
  }, []);

  const { stage, status, empresa_id, owner } = filters;

  const fetchDeals = useCallback(async () => {
    if (authLoading || !launchesUnitId) {
      setLoading(true);
      return;
    }
    if (!user?.id) {
      setDeals([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let query = supabase
        .from('launch_deals')
        .select(
          'id, title, description, stage, status, valor_estimado, next_step, next_step_due_date, owner, source, created_at, updated_at, orulo_building_id, empresa:empresas(id, empresa, company_name)'
        )
        .eq('business_unit_id', launchesUnitId)
        .order('created_at', { ascending: false });

      if (stage)      query = query.eq('stage', stage);
      if (status)     query = query.eq('status', status);
      if (empresa_id) query = query.eq('empresa_id', empresa_id);
      if (owner)      query = query.eq('owner', owner);

      const { data, error: err } = await query;
      if (err) throw err;
      setDeals(data || []);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [user?.id, authLoading, launchesUnitId, stage, status, empresa_id, owner]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  return { deals, loading: loading || authLoading || !launchesUnitId, error, refetch: fetchDeals };
}
